import React from 'react';
import { Play, Pause, Volume2, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FeedbackAudioControlsProps {
  isPlaying: boolean;
  isPaused: boolean;
  isLoading: boolean;
  onPlay: () => void;
  onPause: () => void;
  onResume: () => void;
  className?: string;
}

export const FeedbackAudioControls: React.FC<FeedbackAudioControlsProps> = ({
  isPlaying,
  isPaused,
  isLoading,
  onPlay,
  onPause,
  onResume,
  className
}) => {
  const handleClick = () => {
    if (isPlaying && !isPaused) {
      onPause();
    } else if (isPaused) {
      onResume();
    } else {
      onPlay();
    }
  };

  const getButtonText = () => {
    if (isLoading) return 'Loading audio...';
    if (isPlaying && !isPaused) return 'Pause feedback';
    if (isPaused) return 'Resume feedback';
    return 'Listen to feedback';
  };

  const getIcon = () => {
    if (isLoading) {
      return <Loader2 className="w-4 h-4 animate-spin" />;
    }
    if (isPlaying && !isPaused) {
      return <Pause className="w-4 h-4" />;
    }
    return <Play className="w-4 h-4" />;
  };

  return (
    <div className={cn("flex items-center gap-4", className)}>
      <button
        onClick={handleClick}
        disabled={isLoading}
        className={cn(
          "flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all",
          "bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
          isLoading && "opacity-50 cursor-not-allowed"
        )}
      >
        {getIcon()}
        <span>{getButtonText()}</span>
      </button>
      
      {(isPlaying || isPaused) && (
        <div className="flex items-center gap-2 text-gray-600">
          <Volume2 className="w-4 h-4" />
          <span className="text-sm">Audio feedback playing</span>
        </div>
      )}
    </div>
  );
};